// packages/common/src/sse/useThreadsSSE.ts
import { useCallback, useRef } from 'react';
import { useSSE } from './useSSE';

/**
 * Shared wire shape for every thread message event. `new_message` populates all
 * of it; `message_edited` / `message_deleted` carry only the routing fields
 * (`thread_id`, `thread_type`, `entry_id`, `message_id`) — every field is
 * optional, so the same type describes all three without a lying cast.
 */
export type ThreadMessagePayload = {
  thread_id?: string;
  thread_type?: 'selection' | 'script' | 'content';
  entry_id?: string;
  message_id?: string;
  sender_id?: string;
  sender_role?: string;
};

/** @deprecated Use ThreadMessagePayload — the shape is shared by all three thread events. */
export type NewMessagePayload = ThreadMessagePayload;

/** The two events that change an existing message rather than adding one. */
export type ThreadMessageMutationEvent = 'message_edited' | 'message_deleted';

export type UnreadCountPayload = {
  thread_id?: string;
  thread_type?: 'selection' | 'script' | 'content';
  entry_id?: string;
  unread_count?: number;
};

/**
 * Narrows a raw SSE payload to ThreadMessagePayload, dropping any field whose
 * runtime type doesn't match rather than asserting the shape blind. Everything
 * is optional, so a malformed field degrades to `undefined` and consumers'
 * existing `if (!payload.entry_id) return` guards handle it.
 */
function toThreadMessagePayload(raw: Record<string, unknown>): ThreadMessagePayload {
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  const threadType = str(raw.thread_type);
  return {
    thread_id: str(raw.thread_id),
    thread_type:
      threadType === 'selection' || threadType === 'script' || threadType === 'content'
        ? threadType
        : undefined,
    entry_id: str(raw.entry_id),
    message_id: str(raw.message_id),
    sender_id: str(raw.sender_id),
    sender_role: str(raw.sender_role),
  };
}

interface Options {
  enabled: boolean;
  /** Fires for `new_message` only — a genuinely new message, which DOES advance the thread's last_activity_at. */
  onNewMessage: (payload: ThreadMessagePayload) => void;
  /**
   * Fires for `message_edited` / `message_deleted`. These need the same
   * re-fetch as a new message but are NOT new activity — the BE does not bump
   * `last_activity_at` on edit/delete — so a consumer must not reorder the
   * roster row here, or it jumps to the top and then snaps back on refetch.
   *
   * Omitted: falls back to `onNewMessage` so existing consumers keep receiving
   * live edit/delete updates unchanged.
   */
  onMessageMutated?: (payload: ThreadMessagePayload, event: ThreadMessageMutationEvent) => void;
  onUnreadCount?: (payload: UnreadCountPayload) => void;
}

export function useThreadsSSE({
  enabled,
  onNewMessage,
  onMessageMutated,
  onUnreadCount,
}: Options): void {
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageMutatedRef = useRef(onMessageMutated);
  const onUnreadCountRef = useRef(onUnreadCount);
  onNewMessageRef.current = onNewMessage;
  onMessageMutatedRef.current = onMessageMutated;
  onUnreadCountRef.current = onUnreadCount;

  const messageHandler = useCallback((payload: Record<string, unknown>) => {
    onNewMessageRef.current(toThreadMessagePayload(payload));
  }, []);

  const editedHandler = useCallback((payload: Record<string, unknown>) => {
    const narrowed = toThreadMessagePayload(payload);
    const mutated = onMessageMutatedRef.current;
    if (mutated) mutated(narrowed, 'message_edited');
    else onNewMessageRef.current(narrowed);
  }, []);

  const deletedHandler = useCallback((payload: Record<string, unknown>) => {
    const narrowed = toThreadMessagePayload(payload);
    const mutated = onMessageMutatedRef.current;
    if (mutated) mutated(narrowed, 'message_deleted');
    else onNewMessageRef.current(narrowed);
  }, []);

  const unreadHandler = useCallback((payload: Record<string, unknown>) => {
    onUnreadCountRef.current?.(payload as UnreadCountPayload);
  }, []);

  useSSE('new_message', enabled, messageHandler);
  useSSE('unread_count', enabled, unreadHandler);
  // Edit/delete carry the same {thread_id, thread_type, entry_id, message_id}
  // shape and need the same "re-fetch this thread + its roster row" reaction as
  // a new message — otherwise another viewer keeps showing an edited/deleted
  // message (and a deleted-unread badge overcounts) until a manual refetch.
  useSSE('message_edited', enabled, editedHandler);
  useSSE('message_deleted', enabled, deletedHandler);
}
